import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChatPanel } from "@/components/ChatPanel";
import { Briefcase, Building2, MessageSquare, Search } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 7000;

const formatPreviewTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
};

export default function Messages() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [tab, setTab] = useState("all"); // all | broker | company
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // {id, partner_type, partner_id, partner_name}
  const prevTotalRef = useRef(0);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConvs(data);
      const total = data.reduce((a, c) => a + (c.unread_count || 0), 0);
      // Toast on new unread arrival (only if increased and not first load)
      if (total > prevTotalRef.current && prevTotalRef.current > 0) {
        toast.info(`💬 ${total - prevTotalRef.current} nuovo${total - prevTotalRef.current > 1 ? "i" : ""} messaggio${total - prevTotalRef.current > 1 ? "" : ""}`);
        // also browser notification if permitted
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification("Nuovo messaggio CRM Assicura", { body: "Hai ricevuto messaggi da partner." });
        }
      }
      prevTotalRef.current = total;
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, POLL_MS);
    return () => clearInterval(t);
  }, [loadConvs]);

  // Ask browser permission once
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const openConversation = async (c) => {
    let id = c.id;
    if (!id) {
      try {
        const { data } = await api.post("/chat/conversations/open", {
          partner_type: c.partner_type,
          partner_id: c.partner_id,
        });
        id = data.id;
      } catch (e) { toast.error(formatApiError(e)); return; }
    }
    setSelected({ id, partner_type: c.partner_type, partner_id: c.partner_id, partner_name: c.partner_name });
    loadConvs();
  };

  const filtered = convs
    .filter((c) => tab === "all" || c.partner_type === tab)
    .filter((c) => !search || c.partner_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (!a.last_message_at && !b.last_message_at) return a.partner_name.localeCompare(b.partner_name);
      if (!a.last_message_at) return 1;
      if (!b.last_message_at) return -1;
      return b.last_message_at.localeCompare(a.last_message_at);
    });

  return (
    <div className="space-y-4 animate-fade-up" data-testid="messages-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Messaggistica interna</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Messaggi</h1>
        <p className="text-slate-500 mt-1">Chat in tempo reale con broker, agenzie e compagnie partner.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden grid grid-cols-1 lg:grid-cols-[340px_1fr]" style={{ minHeight: "70vh" }}>
        {/* Left: conversation list */}
        <aside className="border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b border-slate-200 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                data-testid="messages-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca partner…"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex gap-1.5">
              {[
                { v: "all", l: "Tutti" },
                { v: "broker", l: "Broker" },
                { v: "company", l: "Compagnie" },
              ].map((t) => (
                <button
                  key={t.v}
                  data-testid={`tab-${t.v}`}
                  onClick={() => setTab(t.v)}
                  className={`text-xs px-3 py-1.5 rounded-md flex-1 transition-colors ${
                    tab === t.v ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >{t.l}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="conv-list">
            {filtered.length === 0 && (
              <div className="p-6 text-center text-xs text-slate-400">Nessuna conversazione.</div>
            )}
            {filtered.map((c) => {
              const key = c.id || `${c.partner_type}-${c.partner_id}`;
              const isActive = selected?.partner_id === c.partner_id && selected?.partner_type === c.partner_type;
              const Icon = c.partner_type === "broker" ? Briefcase : Building2;
              return (
                <button
                  key={key}
                  data-testid={`conv-${c.partner_type}-${c.partner_id}`}
                  onClick={() => openConversation(c)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-start gap-3 ${
                    isActive ? "bg-blue-50/60" : ""
                  }`}
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    c.partner_type === "broker" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{c.partner_name}</span>
                      <span className="text-[10px] text-slate-400 flex-shrink-0">{formatPreviewTime(c.last_message_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-xs text-slate-500 truncate">
                        {c.last_message_preview || <em className="text-slate-400">Nessun messaggio</em>}
                      </span>
                      {c.unread_count > 0 && (
                        <span data-testid={`unread-${c.partner_id}`} className="bg-blue-600 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center flex-shrink-0">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mt-0.5">{c.partner_type === "broker" ? "Broker / Agenzia" : "Compagnia"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Right: chat panel */}
        <section className="flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>Seleziona una conversazione per iniziare</p>
              </div>
            </div>
          ) : (
            <ChatPanel
              key={selected.id}
              conversationId={selected.id}
              partnerName={selected.partner_name}
              partnerType={selected.partner_type}
              currentUser={user}
              onMessagesChanged={loadConvs}
            />
          )}
        </section>
      </div>
    </div>
  );
}
